import React, { useState, useEffect, useContext } from "react";
import { Form, FormGroup, Label, Input, Button, Row, Col } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import ValetApi from "../api/Api";
import UserContext from "../auth/UserContext";
import { toast } from "react-toastify";
import RegionDropdown from "../components/RegionDropdown";
import LocationDropdown from "../components/LocationDropdown";
import "./AddLocationForm.css";

const validationSchema = Yup.object({
    name: Yup.string().required("Podium name is required"),
    locationId: Yup.string().required("Location is required"),
    parkingCapacity: Yup.number().integer("Capacity must be a whole number").min(0, "Capacity must be 0 or greater").nullable(),
});

const AddPodiumForm = () => {
    const { addPodium } = useContext(UserContext);
    const [regions, setRegions] = useState([]);
    const [locations, setLocations] = useState([]);
    const [selectedRegionId, setSelectedRegionId] = useState("");

    useEffect(() => {
        async function fetchRegions() {
            try {
                const res = await ValetApi.getAllRegions();
                setRegions(res.data.regions);
            } catch {
                toast.error("Error fetching regions.");
            }
        }
        fetchRegions();
    }, []);

    useEffect(() => {
        async function fetchLocations() {
            if (selectedRegionId) {
                try {
                    const res = await ValetApi.getLocationsByRegion(selectedRegionId);
                    let locs = res.data.locations;
                    if (!Array.isArray(locs)) {
                        locs = locs ? [locs] : [];
                    }
                    setLocations(locs);
                } catch (err) {
                    // If 404, set locations to empty array
                    setLocations([]);
                }
            } else {
                setLocations([]);
            }
        }
        fetchLocations();
    }, [selectedRegionId]);

    const formik = useFormik({
        initialValues: { name: "", locationId: "", parkingCapacity: "" },
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            const normalizedCapacity = String(values.parkingCapacity || "").trim();
            const result = await addPodium({
                name: values.name,
                locationId: Number(values.locationId),
                isDeleted: false,
                parkingCapacity: normalizedCapacity === "" ? null : Number(normalizedCapacity),
            });

            if (result?.success) {
                resetForm();
            }
        },
    });

    return (
        <Row className="justify-content-center add-location-page">
            <Col
                xs={12}
                md={7}
                lg={6}>
                <Form
                    className="add-location-form"
                    onSubmit={formik.handleSubmit}>
                    <FormGroup className="add-location-group">
                        <RegionDropdown
                            regions={regions}
                            selectedRegionId={selectedRegionId}
                            onChange={(regionId) => {
                                setSelectedRegionId(regionId);
                                formik.setFieldValue("locationId", "");
                            }}
                        />
                    </FormGroup>
                    <FormGroup className="add-location-group">
                        <LocationDropdown
                            locations={locations}
                            selectedLocationId={formik.values.locationId}
                            onChange={(locationId) => formik.setFieldValue("locationId", locationId)}
                            disabled={locations.length === 0}
                        />
                        {formik.touched.locationId && formik.errors.locationId && <div className="text-danger">{formik.errors.locationId}</div>}
                    </FormGroup>
                    <FormGroup className="add-location-group">
                        <Label htmlFor="name">Podium Name</Label>
                        <Input
                            className="add-location-input"
                            id="name"
                            name="name"
                            type="text"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={formik.touched.name && Boolean(formik.errors.name)}
                            disabled={locations.length === 0}
                        />
                        {formik.touched.name && formik.errors.name && <div className="invalid-feedback">{formik.errors.name}</div>}
                    </FormGroup>
                    <FormGroup className="add-location-group">
                        <Label htmlFor="parkingCapacity">Parking Capacity (Optional)</Label>
                        <Input
                            className="add-location-input"
                            id="parkingCapacity"
                            name="parkingCapacity"
                            type="number"
                            min="0"
                            step="1"
                            value={formik.values.parkingCapacity}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={formik.touched.parkingCapacity && Boolean(formik.errors.parkingCapacity)}
                            disabled={locations.length === 0}
                        />
                        {formik.touched.parkingCapacity && formik.errors.parkingCapacity && <div className="invalid-feedback">{formik.errors.parkingCapacity}</div>}
                    </FormGroup>
                    {selectedRegionId && locations.length === 0 && <div className="add-location-note">No locations available for this region. Please add a location first.</div>}
                    <Button
                        color="primary"
                        className="add-location-submit"
                        type="submit"
                        disabled={locations.length === 0 || !formik.values.locationId}>
                        Add Podium
                    </Button>
                </Form>
            </Col>
        </Row>
    );
};

export default AddPodiumForm;
